import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { RequestFailed, setOutboxUser } from './api/client.js';
import { useOutboxSender } from './api/outbox.js';
import { forgetKept, KEPT_FOR_MS, keepOnDevice, persister, useLoadAhead } from './api/offline.js';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  RouterProvider,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { isAccessRequired, markAccessRequired, useAccessRequired } from './api/access.js';
import { useLiveUpdates } from './api/live.js';
import { useTickSender } from './api/ticks.js';
import { useCurrentUser, useFinishSignOut, useSetupStatus } from './api/session.js';
import { I18nProvider, useT } from './i18n/index.js';
import { Ingredients } from './routes/Ingredients.js';
import { RecipeEditor } from './routes/RecipeEditor.js';
import { RecipeView } from './routes/RecipeView.js';
import { Recipes } from './routes/Recipes.js';
import { Dishes } from './routes/Dishes.js';
import { DishView } from './routes/DishView.js';
import { Menus } from './routes/Menus.js';
import { MenuDetail } from './routes/MenuDetail.js';
import { Today } from './routes/Today.js';
import { Line } from './routes/Line.js';
import { Login } from './routes/Login.js';
import { AccessRequest } from './routes/AccessRequest.js';
import { Trust } from './routes/Trust.js';
import { Settings } from './routes/Settings.js';
import { Setup } from './routes/Setup.js';
import { applyTheme } from './lib/theme.js';
import { VERSION } from './version.js';
import { useVersionWatch } from './lib/freshness.js';
import { RouteError } from './components/UpdatedNotice.js';
import { TourHost } from './components/Tour.js';
// Self-hosted, so a kitchen server with no internet still draws its own type.
import '@fontsource/barlow/400.css';
import '@fontsource/barlow/500.css';
import '@fontsource/barlow/600.css';
import '@fontsource/barlow-condensed/500.css';
import '@fontsource/barlow-condensed/600.css';
import './styles.css';

applyTheme();

// Over HTTPS (or on this machine itself), keep the app on the device so it
// opens with no server. Browsers allow service workers nowhere else.
if (import.meta.env.PROD && 'serviceWorker' in navigator && window.isSecureContext) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}

/**
 * A 401 from anything means the session ended: show the sign-in screen. A
 * device outside the kitchen that isn't (or is no longer) approved gets the
 * request page instead.
 */
function signedOutElsewhere(error: unknown): void {
  // A session that ended, or a device no longer let in from outside: what
  // this device kept goes too, so a revoked phone keeps no recipes.
  const ended =
    error instanceof RequestFailed &&
    error.isUnauthenticated &&
    queryClient.getQueryData(['me']) != null;
  if (error instanceof RequestFailed && error.isUnauthenticated) {
    queryClient.setQueryData(['me'], null);
  }
  if (isAccessRequired(error)) markAccessRequired();
  if (ended || isAccessRequired(error)) {
    const keep = new Set(['setup-status', 'me', 'access-status']);
    queryClient.removeQueries({ predicate: (query) => !keep.has(String(query.queryKey[0])) });
    void forgetKept();
  }
}

/** How often a screen that can't reach the server tries again. */
const RECONNECT_MS = 5_000;

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: signedOutElsewhere }),
  mutationCache: new MutationCache({ onError: signedOutElsewhere }),
  defaultOptions: {
    queries: {
      // A kitchen tablet sits on a counter for hours; refetching on focus keeps
      // the prep list honest without anyone reaching for reload.
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      // Once, for a dropped connection; asking again won't change a no.
      retry: (count, error) => count < 1 && !(error instanceof RequestFailed && error.status < 500),
    },
  },
});

/**
 * Decides what an arriving browser should see: the first-run page on an empty
 * server, the sign-in page when nobody is signed in, and the app otherwise.
 */
function Shell() {
  const navigate = useNavigate();
  const { t } = useT();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const setupStatus = useSetupStatus();
  const currentUser = useCurrentUser();
  const accessRequired = useAccessRequired();

  const needsSetup = setupStatus.data?.needsSetup === true;
  // Only a real "nobody is signed in" counts. A server error is not a sign-out.
  const signedOut = currentUser.isSuccess && currentUser.data === null;
  // Only when there's nothing to show yet; a failed background refresh keeps the screen.
  const unreachable =
    !accessRequired &&
    ((setupStatus.isError && setupStatus.data === undefined) ||
      (currentUser.isError && currentUser.data === undefined));
  // /trust is for tablets that haven't signed in yet, or can't.
  const isPublicRoute =
    pathname === '/login' || pathname === '/line' || pathname === '/setup' || pathname === '/trust';

  useEffect(() => {
    if (!unreachable) return;
    const timer = window.setInterval(() => {
      void setupStatus.refetch();
      void currentUser.refetch();
    }, RECONNECT_MS);
    return () => window.clearInterval(timer);
  }, [unreachable, setupStatus, currentUser]);

  useLiveUpdates(currentUser.data != null);
  // Changes kept on the device while Mise couldn't be reached go out from here.
  setOutboxUser(currentUser.data?.id ?? null);
  useOutboxSender();
  useLoadAhead(currentUser.data != null);
  useVersionWatch();
  // Saved ticks keep being sent whichever screen is open.
  useTickSender();
  useFinishSignOut();

  useEffect(() => {
    if (setupStatus.isPending) return;

    if (needsSetup && pathname !== '/setup') {
      void navigate({ to: '/setup', replace: true });
      return;
    }
    if (!needsSetup && pathname === '/setup') {
      // Whoever just finished setting up is already signed in.
      void navigate({ to: signedOut ? '/login' : '/', replace: true });
      return;
    }
    if (!needsSetup && signedOut && !isPublicRoute) {
      void navigate({ to: '/login', replace: true });
    }
  }, [needsSetup, signedOut, pathname, isPublicRoute, setupStatus.isPending, navigate]);

  if (accessRequired) return <AccessRequest />;

  if (setupStatus.isPending || currentUser.isPending) {
    return (
      <div className="text-ink-soft flex min-h-dvh items-center justify-center">
        {t('common.loading')}
      </div>
    );
  }

  if (unreachable) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center px-5 text-center">
        <h1 className="text-[32px]">{t('shell.unreachableTitle')}</h1>
        <p className="text-ink-soft mt-2">{t('shell.unreachableBody')}</p>
        <button
          type="button"
          className="btn-quiet mt-6"
          onClick={() => {
            void setupStatus.refetch();
            void currentUser.refetch();
          }}
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }

  // The tour lives here rather than in each page, so it keeps its place while
  // it walks from one page to the next. Keyed by person: a cook signing in
  // after someone else gets their own question.
  const inApp = !needsSetup && !isPublicRoute && currentUser.data != null;

  return (
    <>
      <Outlet />
      {inApp ? <TourHost key={currentUser.data!.id} /> : null}
    </>
  );
}

const rootRoute = createRootRoute({ component: Shell });

/** A prep line opens its spec at the amount it asks for: ?amount=2000&unit=g. */
function scaleSearch(search: Record<string, unknown>): { amount?: number; unit?: string } {
  const amount = Number(search.amount);
  return {
    amount: Number.isFinite(amount) && amount > 0 ? amount : undefined,
    unit: typeof search.unit === 'string' ? search.unit : undefined,
  };
}

// Loaded when first opened: the fonts and PDF code are only needed there.
const DishCardPage = lazyRouteComponent(() => import('./routes/DishCard.js'), 'DishCardPage');

const routeTree = rootRoute.addChildren([
  createRoute({ getParentRoute: () => rootRoute, path: '/', component: Today }),
  createRoute({ getParentRoute: () => rootRoute, path: '/recipes', component: Recipes }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/recipes/new',
    component: RecipeEditor,
    // A new component rather than a prep: ?type=component. Dishes start under Dishes.
    validateSearch: (search: Record<string, unknown>): { type?: 'prep' | 'component' } => ({
      type: search.type === 'prep' || search.type === 'component' ? search.type : undefined,
    }),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/recipes/$recipeId',
    component: RecipeView,
    validateSearch: scaleSearch,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/recipes/$recipeId/edit',
    component: RecipeEditor,
  }),
  // An older link to a dish card; it moves on to the Dishes tab.
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/recipes/$recipeId/card',
    component: DishCardPage,
  }),

  // Dishes have pages of their own, under their own tab.
  createRoute({ getParentRoute: () => rootRoute, path: '/dishes', component: Dishes }),
  createRoute({ getParentRoute: () => rootRoute, path: '/dishes/new', component: RecipeEditor }),
  createRoute({ getParentRoute: () => rootRoute, path: '/dishes/$dishId', component: DishView }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/dishes/$dishId/recipe',
    component: RecipeView,
    validateSearch: scaleSearch,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/dishes/$dishId/edit',
    component: RecipeEditor,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/dishes/$dishId/card',
    component: DishCardPage,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/menus', component: Menus }),
  createRoute({ getParentRoute: () => rootRoute, path: '/menus/$menuId', component: MenuDetail }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/menus/$menuId/overview',
    component: lazyRouteComponent(() => import('./routes/MenuOverview.js'), 'MenuOverviewPage'),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/ingredients', component: Ingredients }),
  createRoute({ getParentRoute: () => rootRoute, path: '/setup', component: Setup }),
  createRoute({ getParentRoute: () => rootRoute, path: '/login', component: Login }),
  createRoute({ getParentRoute: () => rootRoute, path: '/trust', component: Trust }),
  createRoute({ getParentRoute: () => rootRoute, path: '/line', component: Line }),
  createRoute({ getParentRoute: () => rootRoute, path: '/settings', component: Settings }),
]);

// No view transitions: where the browser can't run one (a dimmed or covered
// tablet), it aborts it and the page change never finishes. Pages animate in
// with plain CSS instead; see .app-main in styles.css.
// A page that can't open says why; after an update, that's a reload away.
const router = createRouter({ routeTree, defaultErrorComponent: RouteError });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Missing #root element');

createRoot(rootElement).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: KEPT_FOR_MS,
        // A new version may shape things differently: start its cache afresh.
        buster: VERSION,
        dehydrateOptions: {
          // "Nobody signed in" is never kept: restored after signing in again,
          // it would send the person back to the sign-in page.
          shouldDehydrateQuery: (query) =>
            keepOnDevice(query.queryKey, query.state.data !== undefined) &&
            !(query.queryKey[0] === 'me' && query.state.data === null),
          // Changes waiting have their own, safer place (lib/outbox.ts).
          shouldDehydrateMutation: () => false,
        },
      }}
      // What was kept shows at once, but it may be hours old: ask again for
      // all of it now, rather than trusting it as fresh.
      onSuccess={() => void queryClient.invalidateQueries()}
    >
      <I18nProvider>
        <RouterProvider router={router} />
      </I18nProvider>
    </PersistQueryClientProvider>
  </StrictMode>,
);
